import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import StepRow from '../components/StepRow';
import PartnerEntry from '../components/PartnerEntry';

export default function Sell() {
  return (
    <>
      <PageHero
        eyebrow="Selling path"
        title="Sell your guard company on"
        emphasis="your own terms."
        lede="Selling is the most sensitive transaction an owner ever runs. Customers, employees, and competitors don't need to know you're considering it. OfficerCapital is built around that reality — anonymous listings, vetted acquirers, no broker between you and the conversation."
      >
        <a href="https://officercapital.com/register" className="oc-btn oc-btn--primary">List your company</a>
        <Link to="/how-it-works" className="oc-btn oc-btn--link">See how it works →</Link>
      </PageHero>

      {/* WHY OWNERS SELL HERE */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Why sell on OfficerCapital</div>
              <h2 className="oc-h2">
                The broker model is <em className="oc-em">backwards</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Traditional brokers take 8 to 12 percent of your sale price — on a $5M deal, that's $400K to $600K of value
              you negotiated for, paid to someone who was supposed to get out of the way after the introduction.
              We take none of it.
            </p>
          </div>

          <div className="oc-grid-3">
            <BenefitCard
              label="No success fee"
              body="Close on whatever number you negotiate. Whatever you sell for is what you keep — minus your own legal and accounting, of course."
            />
            <BenefitCard
              label="True anonymity"
              body="Your company name never appears on the listing. Customer names, employee headcount details, and contracts stay sealed until you choose to share."
            />
            <BenefitCard
              label="Per-acquirer access"
              body="Approving one acquirer reveals you to that acquirer only. Everyone else still sees an anonymous listing."
            />
          </div>
        </div>
      </section>

      {/* WHAT STAYS HIDDEN */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">What stays hidden</div>
              <h2 className="oc-h2">
                The details that <em className="oc-em">give you away</em>, until you say so.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Even careful sellers leak identity through specifics — a known anchor customer, a unique geography,
                a distinctive contract structure. OfficerCapital's listing template is built to surface enough for
                acquirers to evaluate, without exposing the things that uniquely identify you.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <ListPoint title="Customer names" body="Customer concentration is shown by category and size, not by name." />
                <hr className="oc-rule" />
                <ListPoint title="Employee identities" body="Officer counts and tenure summaries only — no names, no key-person disclosures." />
                <hr className="oc-rule" />
                <ListPoint title="Geographic specificity" body="Geography shown by state and metro size class. Exact cities revealed after approval." />
                <hr className="oc-rule" />
                <ListPoint title="Document-level data" body="P&amp;Ls, contracts, balance sheets — sealed until per-acquirer access is granted." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO'S ON THE OTHER SIDE */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-4">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Who you'll talk to</div>
              <h2 className="oc-h2">
                Acquirers who've <em className="oc-em">closed</em> in this industry before.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Strategic buyers, PE-backed roll-ups, and individual investors building security platforms.
              The unlock fee for acquirers is $1,500 per deal — three times the rate for lenders or investors —
              so the people who reach out are the ones who've already done the diligence on the category.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">How a sale runs on OfficerCapital</div>
              <h2 className="oc-h2">
                Three steps, on <em className="oc-em">your timeline</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              You can move from a first listing to multiple acquirer conversations in under three weeks.
              Or you can sit on it. The listing waits for you.
            </p>
          </div>

          <div className="oc-grid-3-wide">
            <StepRow number="01" title="List anonymously">
              Headline financials, customer mix by category, geography, what you're looking for in a sale. The platform anonymizes everything that could identify you.
            </StepRow>
            <StepRow number="02" title="Acquirers request access">
              Vetted acquirers review your listing and pay to unlock the full file. You see who they are first — name, fund, prior deals.
            </StepRow>
            <StepRow number="03" title="Approve and engage">
              Approve only the acquirers worth your time. Messaging opens. Take it offline whenever you want — we don't sit between you and the deal.
            </StepRow>
          </div>
        </div>
      </section>

      {/* OTHER PATHS */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-eyebrow-gray oc-mb-3">Other paths</div>
          <div className="oc-grid-2">
            <RelatedPathCard
              to="/financing"
              eyebrow="Path 01"
              title="You need financing instead"
              body="Working capital or credit lines while you keep operating? See how lenders connect on OfficerCapital."
            />
            <RelatedPathCard
              to="/investors"
              eyebrow="Path 02"
              title="You want a partner, not a buyer"
              body="Considering a minority investor or recap instead of a full sale? See the investors path."
            />
          </div>
        </div>
      </section>

      <PartnerEntry />
    </>
  );
}

function BenefitCard({ label, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}
function ListPoint({ title, body }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}
function RelatedPathCard({ to, eyebrow, title, body }) {
  return (
    <Link to={to} className="oc-card oc-card--linked" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
      <div className="oc-eyebrow oc-mb-2">{eyebrow}</div>
      <h3 className="oc-h3" style={{ marginBottom: 8 }}>{title}</h3>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>{body}</p>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>Read more →</span>
    </Link>
  );
}
